// Local date + hour <-> ISO timestamp helpers for the OQ-45 send scheduler.
// Admins pick a calendar date and an hour in THEIR browser's local time; the API
// stores an absolute instant. Sends go out on the hourly OQ cron tick, so hours
// are the only precision offered.

export const HOUR_OPTIONS: Array<[number, string]> = Array.from({ length: 24 }, (_, h) => {
    const twelve = h % 12 === 0 ? 12 : h % 12;
    return [h, `${twelve}:00 ${h < 12 ? 'AM' : 'PM'}`];
});

const pad = (n: number): string => String(n).padStart(2, '0');

/** YYYY-MM-DD for an <input type="date"> in local time. */
export const toDateInputValue = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Build the ISO instant for a local YYYY-MM-DD + hour; null when the date is unparseable. */
export const toStartAtISO = (dateValue: string, hour: number): string | null => {
    const [y, m, day] = dateValue.split('-').map(Number);
    if (!y || !m || !day) return null;
    const local = new Date(y, m - 1, day, hour, 0, 0, 0);
    return Number.isNaN(local.getTime()) ? null : local.toISOString();
};

/** Split an ISO instant back into the local date value + hour for the form. */
export const fromStartAtISO = (iso: string): { dateValue: string; hour: number } | null => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return { dateValue: toDateInputValue(date), hour: date.getHours() };
};

/** Default start: tomorrow at 9 AM local. */
export const defaultStart = (now: Date = new Date()): { dateValue: string; hour: number } => {
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    return { dateValue: toDateInputValue(tomorrow), hour: 9 };
};

/** Human-readable "Tue, Sep 22, 9:00 AM" style stamp for a stored instant. */
export const formatSendAt = (value: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};
